import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  IoWarningOutline, 
  IoCheckmarkCircleOutline, 
  IoPersonAddOutline,
  IoPencilOutline,
  IoTrashOutline,
  IoCloseOutline,
  IoSaveOutline,
  IoRefreshOutline,
  IoPersonOutline,
  IoSearchOutline,
  IoCreateOutline,
  IoFilterOutline,
  IoGridOutline,
  IoListOutline
} from 'react-icons/io5';
import './EditUser.css';
import { API_URL } from '../utils/networkConfig';

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form state
  const [form, setForm] = useState({ name: '', email: '', password: '', role_id: 3 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(20);

  const roleNames = {
    1: 'Admin',
    2: 'Home Viewer',
    3: 'Game Show Player'
  };

  const getRoleBadgeClass = (roleId) => {
    switch(roleId) {
      case 1: return 'admin';
      case 2: return 'home-viewer';
      case 3: return 'game-player';
      default: return 'game-player';
    }
  };

  const navigate = useNavigate();

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please log in to view users.');
        return;
      }

      const res = await fetch(`${API_URL}/api/game-sessions/admin/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      console.log('Fetch users API response:', data);
      
      if (res.ok) {
        // Handle both array format and object with users property
        const usersList = Array.isArray(data) ? data : (data.users || []);
        console.log('Users list:', usersList);
        
        // Show all users (removed filter for role_id = 3)
        setUsers(usersList);
      } else {
        setError(data.error || 'Failed to fetch users.');
      }
    } catch (err) {
      console.error('Fetch users error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search and role
  useEffect(() => {
    let filtered = users;
    
    if (searchTerm) {
      filtered = filtered.filter(user => 
        (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role_id === parseInt(roleFilter));
    }
    
    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [users, searchTerm, roleFilter]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Load users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError('');
    if (success) setSuccess('');
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setForm({ ...form, password });
  };

  const resetForm = () => {
    setForm({ name: '', email: '', password: '', role_id: 3 });
    setError('');
    setSuccess('');
  };

  // Create user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('authToken');
      
      const res = await fetch(`${API_URL}/api/auth/admin/create-gameshow-user`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(form),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSuccess('User created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchUsers(); // Refresh the list
      } else {
        setError(data.error || 'Failed to create user.');
      }
    } catch (err) {
      console.error('Create user error:', err);
      setError('Network error. Please check if the server is running.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit user
  const handleEditUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('authToken');
      
      const res = await fetch(`${API_URL}/api/game-sessions/admin/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          role_id: parseInt(form.role_id)
        }),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSuccess('User updated successfully!');
        setShowEditModal(false);
        resetForm();
        setSelectedUser(null);
        fetchUsers(); // Refresh the list
      } else {
        setError(data.error || 'Failed to update user.');
      }
    } catch (err) {
      console.error('Edit user error:', err);
      setError('Network error. Please check if the server is running.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('authToken');
      
      const res = await fetch(`${API_URL}/api/game-sessions/admin/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSuccess('User deleted successfully!');
        setShowDeleteModal(false);
        setSelectedUser(null);
        fetchUsers(); // Refresh the list
      } else {
        setError(data.error || 'Failed to delete user.');
      }
    } catch (err) {
      console.error('Delete user error:', err);
      setError('Network error. Please check if the server is running.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setForm({
      name: user.name,
      email: user.email,
      password: '',
      role_id: user.role_id
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedUser(null);
    resetForm();
  };

  return (
    <div className="user-management-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title">User Management</h1>
            <p className="page-subtitle">Manage and organize users for the AI Game Show platform</p>
          </div>
          <div className="header-actions">
            <button 
              onClick={openCreateModal} 
              className="primary-action-btn"
            >
              <IoPersonAddOutline className="btn-icon" />
              Create New User
            </button>
          </div>
        </div>
      </div>

      {/* Control Panel */}
      <div className="control-panel">
        <div className="panel-section">
          <div className="search-section">
            <div className="search-field">
              <IoSearchOutline className="search-field-icon" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-field-input"
              />
            </div>
            <div className="filter-section">
              <IoFilterOutline className="filter-icon" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Roles</option>
                <option value="1">Admins</option>
                <option value="2">Home Viewers</option>
                <option value="3">Game Players</option>
              </select>
            </div>
          </div>
          
          <div className="panel-actions">
            <div className="view-toggle">
              <button
                onClick={() => setViewMode('table')}
                className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              >
                <IoListOutline />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              >
                <IoGridOutline />
              </button>
            </div>
            <button onClick={fetchUsers} className="refresh-action-btn" disabled={loading}>
              <IoRefreshOutline className={`refresh-icon ${loading ? 'spinning' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Users Display */}
      <div className="users-display">
        {/* Stats Bar */}
        <div className="stats-bar">
          <div className="stats-info">
            <span className="stats-count">{filteredUsers.length}</span>
            <span className="stats-label">users found</span>
            {totalPages > 1 && (
              <>
                <span className="stats-separator">•</span>
                <span className="stats-pagination">
                  Page {currentPage} of {totalPages}
                </span>
              </>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading-display">
            <div className="loading-spinner"></div>
            <p className="loading-text">Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-display">
            <div className="empty-icon">
              <IoPersonOutline />
            </div>
            <h3 className="empty-title">No users found</h3>
            <p className="empty-message">No users match your current search criteria.</p>
          </div>
        ) : viewMode === 'table' ? (
          <div className="table-display">
            <table className="users-data-table">
              <thead>
                <tr>
                  <th>User Details</th>
                  <th>Role</th>
                  <th>Score</th>
                  <th>Answers</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map(user => (
                  <tr key={user.id} className="user-row">
                    <td className="user-details-cell">
                      <div className="user-avatar">
                        <IoPersonOutline />
                      </div>
                      <div className="user-info">
                        <div className="user-name-text">{user.name}</div>
                        <div className="user-email-text">{user.email}</div>
                      </div>
                    </td>
                    <td className="role-cell">
                      <span className={`role-tag ${getRoleBadgeClass(user.role_id)}`}>
                        {roleNames[user.role_id]}
                      </span>
                    </td>
                    <td className="score-cell">
                      <span className="score-value">{user.total_score || 0}</span>
                    </td>
                    <td className="answers-cell">
                      <span className="answers-value">{user.total_answers || 0}</span>
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          onClick={() => openEditModal(user)}
                          className="edit-action-btn"
                          title="Edit User"
                        >
                          <IoPencilOutline />
                        </button>
                        <button
                          onClick={() => openDeleteModal(user)}
                          className="delete-action-btn"
                          title="Delete User"
                          disabled={user.role_id === 1}
                        >
                          <IoTrashOutline />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid-display">
            {currentUsers.map(user => (
              <div key={user.id} className="user-card">
                <div className="card-header">
                  <div className="user-avatar-large">
                    <IoPersonOutline />
                  </div>
                  <div className="card-actions">
                    <button
                      onClick={() => openEditModal(user)}
                      className="card-action-btn edit"
                      title="Edit User"
                    >
                      <IoPencilOutline />
                    </button>
                    <button
                      onClick={() => openDeleteModal(user)}
                      className="card-action-btn delete"
                      title="Delete User"
                      disabled={user.role_id === 1}
                    >
                      <IoTrashOutline />
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <h3 className="card-name">{user.name}</h3>
                  <p className="card-email">{user.email}</p>
                  <div className="card-stats">
                    <div className="stat-item">
                      <span className="stat-label">Score</span>
                      <span className="stat-value">{user.total_score || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Answers</span>
                      <span className="stat-value">{user.total_answers || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="card-footer">
                  <span className={`card-role-tag ${getRoleBadgeClass(user.role_id)}`}>
                    {roleNames[user.role_id]}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <div className="pagination-controls">
            <button 
              className="pagination-btn prev" 
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>
            
            <div className="pagination-pages">
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                return (
                  <button
                    key={page}
                    className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            <button 
              className="pagination-btn next" 
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="notification error">
          <IoWarningOutline className="notification-icon" />
          <span className="notification-text">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="notification success">
          <IoCheckmarkCircleOutline className="notification-icon" />
          <span className="notification-text">{success}</span>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-backdrop" onClick={closeModals}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Create New User</h3>
              <button onClick={closeModals} className="modal-close-btn">
                <IoCloseOutline />
              </button>
            </div>
            
            <form onSubmit={handleCreateUser} className="modal-form">
              <div className="form-field">
                <label className="field-label">Full Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter full name"
                  value={form.name}
                  onChange={handleFormChange}
                  className="field-input"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-field">
                <label className="field-label">Email Address</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  value={form.email}
                  onChange={handleFormChange}
                  className="field-input"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-field">
                <label className="field-label">Password</label>
                <div className="password-field">
                  <input
                    type="text"
                    name="password"
                    placeholder="Password (min 6 characters)"
                    value={form.password}
                    onChange={handleFormChange}
                    className="field-input"
                    required
                    disabled={isSubmitting}
                  />
                  <button 
                    type="button" 
                    onClick={generatePassword}
                    className="generate-btn"
                    disabled={isSubmitting}
                  >
                    Generate
                  </button>
                </div>
              </div>
              
              <div className="form-field">
                <label className="field-label">User Role</label>
                <select
                  name="role_id"
                  value={form.role_id}
                  onChange={handleFormChange}
                  className="field-select"
                  disabled={isSubmitting}
                >
                  <option value={1}>Admin</option>
                  <option value={2}>Home Viewer</option>
                  <option value={3}>Game Show Player</option>
                </select>
              </div>
              
              <div className="modal-actions">
                <button type="button" onClick={closeModals} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className="submit-btn">
                  {isSubmitting ? (
                    <>
                      <div className="btn-spinner"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <IoSaveOutline className="btn-icon" />
                      Create User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-backdrop" onClick={closeModals}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit User</h3>
              <button onClick={closeModals} className="modal-close-btn">
                <IoCloseOutline />
              </button>
            </div>
            
            <form onSubmit={handleEditUser} className="modal-form">
              <div className="form-field">
                <label className="field-label">Full Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter full name"
                  value={form.name}
                  onChange={handleFormChange}
                  className="field-input"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-field">
                <label className="field-label">Email Address</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  value={form.email}
                  onChange={handleFormChange}
                  className="field-input"
                  required
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-field">
                <label className="field-label">User Role</label>
                <select
                  name="role_id"
                  value={form.role_id}
                  onChange={handleFormChange}
                  className="field-select"
                  disabled={isSubmitting}
                >
                  <option value={1}>Admin</option>
                  <option value={2}>Home Viewer</option>
                  <option value={3}>Game Show Player</option>
                </select>
              </div>
              
              <div className="modal-actions">
                <button type="button" onClick={closeModals} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className="submit-btn">
                  {isSubmitting ? (
                    <>
                      <div className="btn-spinner"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <IoSaveOutline className="btn-icon" />
                      Update User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <div className="modal-backdrop" onClick={closeModals}>
          <div className="modal-dialog danger" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete User</h3>
              <button onClick={closeModals} className="modal-close-btn">
                <IoCloseOutline />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="warning-display">
                <IoWarningOutline className="warning-icon" />
                <div className="warning-content">
                  <p className="warning-title">Are you sure you want to delete this user?</p>
                  <div className="user-preview">
                    <strong>{selectedUser.name}</strong>
                    <span>{selectedUser.email}</span>
                  </div>
                  <p className="warning-message">This action cannot be undone.</p>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button type="button" onClick={closeModals} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleDeleteUser} disabled={isSubmitting} className="danger-btn">
                {isSubmitting ? (
                  <>
                    <div className="btn-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <IoTrashOutline className="btn-icon" />
                    Delete User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers; 